import './time-section.css';

import EventEmitter from 'eventemitter3';
import { INewName } from '../../../interfaces/global.interfaces';
import { OrderType, TimeSelection, Pages } from '../../../types/enums';
import { PillBar } from '../PillBar/pill-bar';
import { IPillBarData } from '../PillBar/pill-bar.interface';
import { ScheduleLater } from '../../modals/ScheduleLater/schedule-later';
import { ITimeSection } from './time-selection.interface';
import { Data } from '../../../models/data';
import { Order } from '../../../models/order';
import { OnlineOrderingUtil } from "../../../online-ordering/online-ordering-util";
import { OnlineOrderingService } from '../../../services/online-ordering-service';
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { DataLayerService } from '../../../services/data-layer-service';
import { DialogCreators } from "../../../utils/dialog-creators";
import { IDateStartEndTime } from '../../../utils/util.interface';

/**
 * Manages the time selection section of the checkout process.
 */
export class TimeSection {
    private readonly _item: ITimeSection;
    private readonly _element: HTMLElement;
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _order: Order;
    private readonly _onlineOrderingService: OnlineOrderingService;
    private readonly _dataLayerService: DataLayerService;
    private readonly _scheduleLater: ScheduleLater;
    private readonly _idPrefix: string;
    private readonly _onTimeChange?: (type: TimeSelection, futureDate?: Date, futureMinute?: number) => void;
    private readonly _getOrderTypeId: () => OrderType;
    private _checkOrderTimeInterval: ReturnType<typeof setInterval> | null = null;

    /**
     * Initializes an instance of the class.
     * @param {EventEmitter} eventBus - The event bus for handling events.
     * @param {Data} data - The data service containing store and order information.
     * @param {Order} order - The order instance managing the current order state.
     * @param {OnlineOrderingService} onlineOrderingService - The service for handling online ordering operations.
     * @param {string} [idPrefix="checkout"] - Prefix for all DOM element IDs, allowing multiple instances without ID conflicts.
     * @param {() => OrderType} [getOrderTypeId] - Optional override to source the order type from somewhere other than the order model (e.g. a pending, not-yet-applied selection). Defaults to reading from `order`.
     */
    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, order: Order, onlineOrderingService: OnlineOrderingService, dataLayerService: DataLayerService, idPrefix: string = "checkout", onTimeChange?: (type: TimeSelection, futureDate?: Date, futureMinute?: number) => void, getOrderTypeId?: () => OrderType) {
        this._idPrefix = idPrefix;
        this._onTimeChange = onTimeChange;
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._order = order;
        this._onlineOrderingService = onlineOrderingService;
        this._dataLayerService = dataLayerService;
        this._getOrderTypeId = getOrderTypeId ?? (() => this._order.getProperty("OrderTypeID"));
        this._item = this._createInitialModel();
        this._element = this._createHtmlElement();
        this._scheduleLater = new ScheduleLater(this._eventBus, this._data, this._context, this._localizationService, this._order, this._onlineOrderingService, this._dataLayerService, this._idPrefix, (date, minute) => this._onScheduleLaterFutureDateSet(date, minute), this._getOrderTypeId);
        this._setupEventListeners();
        this._setCheckOrderTimeInterval();
    }

    /**
     * Returns the root element of the time section.
     */
    public get element(): HTMLElement {
        return this._element;
    }

    /**
     * Retrieves the selected time ID.
     * @returns {TimeSelection} The selected time ID.
     */
    public getTimeSelection(): TimeSelection {
        return this._item.timeId;
    }

    /**
     * Clears the timer for limited-time selections.
     */
    public clearLimitedTimesTimer(): void {
        this._scheduleLater.clearLimitedTimesTimer();
    }

    /**
     * Confirms the order's holiday-throttled time slot is still reserved; true when not throttled.
     */
    public confirmLimitedTimeReservationAsync(): Promise<boolean> {
        return this._scheduleLater.confirmLimitedTimeReservationAsync();
    }

    /**
     * Resets the time section item to its initial state.
     */
    public resetItemModel(): void {
        this._item.timeId = TimeSelection.ASAP;
        this._scheduleLater.resetItemModel();
    }

    /**
     * Sets whether the periodic check for order time validity is paused.
     * @param paused
     */
    public setCheckOrderTimeIntervalPaused(paused: boolean): void {
        this._item.checkOrderTimeIntervalIsPaused = paused;
    }

    private readonly _onScheduleLaterFutureDateSet = (date: Date, minute: number): void => {
        this._item.futureDate = date;
        this._item.futureMinute = minute;
        this._onTimeChange?.(TimeSelection.LATER, date, minute);
        this._updateTimeOnHeader();
    }

    /**
     * Returns the date selected in the "Schedule for Later" picker, or null when ordering ASAP.
     * Unlike getTimeInfo().futureDate, this reflects a date change before a time has been picked.
     */
    public getSelectedDate(): Date | null {
        return this._item.timeId === TimeSelection.LATER ? this._scheduleLater.getSelectedDate() : null;
    }

    /**
     * Locks or unlocks the "Schedule for Later" date picker (e.g. for payment types that only allow today).
     */
    public setDateLocked(locked: boolean): void {
        this._scheduleLater.setDateLocked(locked);
    }

    public getTimeInfo(): Pick<ITimeSection, 'timeId' | 'futureDate' | 'futureMinute'> {
        return {
            timeId: this._item.timeId,
            futureDate: this._item.futureDate,
            futureMinute: this._item.futureMinute
        };
    }

    /**
     * Sets up event listeners for time-related events.
     */
    private _setupEventListeners() {
        this._eventBus.on(EventTypes.TIME_CHANGED, this._handleTimeChanged);
        this._eventBus.on(EventTypes.LIMITED_TIME_EXPIRED, this._handleLimitedTimeExpired);
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, this._handleOrderTypeChanged);
        this._eventBus.on(EventTypes.LOCATION_CHANGE, (page: Pages) => {
            if (page === Pages.CHECKOUT)
                this._item.checkOrderTimeIntervalIsPaused = false;
            else
                this._item.checkOrderTimeIntervalIsPaused = true;
        });
    }

    /**
     * Creates the initial time section model.
     */
    private _createInitialModel(): ITimeSection {
        return {
            timeId: TimeSelection.ASAP,
            checkOrderTimeIntervalIsPaused: false,
            selectedTimeIsExpired: false,
            futureDate: null,
            futureMinute: null
        };
    }

    /**
     * Creates the HTML structure for the time section.
     */
    private _createHtmlElement(): HTMLElement {
        const p = this._idPrefix;
        const html = `
            <div class="time-section">
                <div class="checkout-header checkout-header-extra">
                    <h2 id="p_${p}_time_header">Pick Up Time</h2>
                    <span id="spn_${p}_wait_time" class="update-btn button-link" aria-live="polite" aria-atomic="true">25 Minutes</span>
                </div>

                <div id="div_${p}_time_options" class="time-section-options"></div>
                <p id="spn_${p}_time_error" class="error-text hidden"></p>

                <div id="div_${p}_time_later" class="hidden" role="tabpanel" aria-labelledby="li_${p}_times_${TimeSelection.LATER}"></div>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    /**
     * Handles the time change event by updating the time header.
     */
    private readonly _handleTimeChanged = (): void => {
        this._updateTimeOnHeader();
    }

    /**
     * Handles the expiration of a limited-time selection.
     * Resets the time option to ASAP and updates the header.
     */
    private readonly _handleLimitedTimeExpired = (): void => {
        this._item.selectedTimeIsExpired = true;
        const asapButton = this._element.querySelector(`#li_${this._idPrefix}_times_${TimeSelection.ASAP}`) as HTMLButtonElement;
        asapButton?.click();
        this._updateTimeOnHeader();
    }

    /**
     * Handles the order type change event by updating the order type header.
     */
    private readonly _handleOrderTypeChanged = (): void => {
        this._renderOrderTypeHeader();
    }

    /**
     * Re-renders everything that depends on the order type (header, promise time, ASAP availability,
     * and the "Schedule for Later" options). Callers with a pending, not-yet-applied order type
     * selection (e.g. OrderTypeAndTime) should invoke this whenever that selection changes.
     */
    public refreshForOrderType(): void {
        this._clearErrorMessage();
        this._renderOrderTypeHeader();
        this._updateTimeOnHeader();
        this._renderTimeOptions();
        this._handleTimeOptionSelection(this._item.timeId);

        if (this._item.timeId === TimeSelection.LATER)
            this._scheduleLater.refresh();
    }

    /**
     * Resolves the order type subtype for the currently effective order type (see _getOrderTypeId).
     * Returns the order's own cached subtype when it matches the real order type, to avoid any
     * behavior change for callers that don't override the order type.
     */
    private _getOrderTypeSubType(): any {
        const orderTypeId = this._getOrderTypeId();
        if (orderTypeId === this._order.getProperty("OrderTypeID"))
            return this._order.getProperty("OrderTypeSubType");

        return OnlineOrderingUtil.getOrderType(orderTypeId, this._data.getProperty("OrderTypeSubTypes") ?? []) ?? this._order.getProperty("OrderTypeSubType");
    }

    /**
     * Resolves the wait time for the currently effective order type (see _getOrderTypeId).
     * Returns the order's own cached wait time when it matches the real order type, to avoid any
     * behavior change for callers that don't override the order type.
     */
    private _getWaitTime(): number {
        const orderTypeId = this._getOrderTypeId();
        if (orderTypeId === this._order.getProperty("OrderTypeID"))
            return this._order.getProperty("WaitTime");

        return OnlineOrderingUtil.getWaitTimeByOrderType(this._data, this._getOrderTypeSubType());
    }

    /**
     * Initializes the time section asynchronously.
     */
    public init(initialFutureDate?: Date | null, initialFutureMinute?: number): void {
        // Seed from caller-provided data first, then fall back to _order (checkout compat).
        const futureDate = initialFutureDate === undefined ? this._order.getProperty("FutureDate") : initialFutureDate;
        const futureMinute = initialFutureMinute ?? this._order.getProperty("FutureMinute");

        if (futureDate && futureMinute !== undefined && futureMinute !== -1) {
            this._item.timeId = TimeSelection.LATER;
            this._item.futureDate = futureDate;
            this._item.futureMinute = futureMinute;
        }

        this._clearErrorMessage();
        this._renderOrderTypeHeader();
        this._updateTimeOnHeader();
        this._renderTimeOptions();
        this._handleTimeOptionSelection(this._item.timeId);
    }

    /**
     * Updates the order type header based on the current order type.
     */
    private _renderOrderTypeHeader(): void {
        const span = this._element.querySelector<HTMLElement>(`#p_${this._idPrefix}_time_header`);
        if (!span)
            return;

        const isDelivery = this._getOrderTypeId() === OrderType.DELIVERY;
        span.setAttribute("ltag", isDelivery ? "DeliveryTime" : "PickUpTime");
        span.innerText = this._localizationService.names(isDelivery ? "DeliveryTime" : "PickUpTime");
    }

    /**
     * Builds the promise time display string for the order.
     * If a buffer time (PBT) is configured, returns a range (e.g. "20-25");
     * otherwise returns the wait time as a plain string (e.g. "20").
     *
     * @returns {string} The formatted promise time text.
     */
    private _getPromiseTimeText(): string {
        const orderTypeId = this._getOrderTypeId();
        const orderTypeSubTypes: any[] = this._data.getProperty("OrderTypeSubTypes") ?? [];
        const subType = orderTypeSubTypes.find(x => x.OrderTypeId === orderTypeId);
        const waitTime = subType?.WaitTime ?? this._order.getProperty("WaitTime");
        const bufferTime = this._data.getProperty("Settings").PBT ?? 0;

        return bufferTime > 0 ? `${waitTime}-${waitTime + bufferTime}` : waitTime.toString();
    }

    /**
     * Updates the pickup time display in the header based on the selected time option.
     * If ASAP is selected, it displays the wait time. Otherwise, it formats and shows the scheduled time.
     */
    private _updateTimeOnHeader(): void {
        const span = this._element.querySelector<HTMLSpanElement>(`#spn_${this._idPrefix}_wait_time`);
        if (!span)
            return;

        if (this._item.timeId === TimeSelection.ASAP) {
            const waitTime = this._getPromiseTimeText();

            // Generate localized time text
            const languages = this._localizationService.getSupportedLanguages();
            const nameObj: INewName[] = languages.map(lang => {
                const tempText = this._localizationService.names("Minutes_Number", lang).replace("{{minutes}}", waitTime);
                return { lc: lang, text: tempText };
            });

            // Set the translated text attributes
            const text = this._localizationService.names("Minutes_Number").replace("{{minutes}}", waitTime);
            span.setAttribute("ltagj", Util.toLtagj(nameObj));
            span.innerText = text;
        } else {
            let text = "";

            if (this._item.futureDate && this._item.futureMinute !== null) {
                const date = new Date(this._item.futureDate.setHours(0, 0, 0, 0));
                date.setMinutes(this._item.futureMinute);
                text = Util.formatDateTimeFull(date);
            }

            span.removeAttribute("ltagj");
            span.innerText = text;
        }
    }

    /**
     * Renders the time selection options for checkout.
     * Creates options for ASAP and "Schedule for Later" selections.
     */
    private _renderTimeOptions(): void {
        const p = this._idPrefix;
        const container = this._element.querySelector<HTMLElement>(`#div_${p}_time_options`);
        if (!container)
            return;

        container.innerHTML = "";

        const data: IPillBarData = {
            buttonId: `${p}_times`,
            ariaLabelledBy: `p_${p}_time_header`,
            buttons: []
        };

        const waitTime = this._getPromiseTimeText();

        // Generate localized text for ASAP option
        const nameObj: INewName[] = this._localizationService.getSupportedLanguages().map(lang => {
            const tempText = this._localizationService.names("ASAPEstimatedTime", lang).replace("{{minutes}}", waitTime);
            return { lc: lang, text: tempText };
        });

        // Determine if ASAP ordering is available
        const isAsapAvailable = this._isAsapAvailable();
        if (!isAsapAvailable)
            this._item.timeId = TimeSelection.LATER;

        // Add ASAP selection option
        data.buttons.push({
            id: TimeSelection.ASAP.toString(),
            text: this._localizationService.names("ASAPEstimatedTime").replace("{{minutes}}", waitTime),
            selected: this._item.timeId === TimeSelection.ASAP,
            ltagj: nameObj,
            disabled: !isAsapAvailable,
            // The error span explains why ASAP is unavailable, so it describes the ASAP tab, not "Later".
            ariaDescribedBy: `spn_${p}_time_error`,
            function: () => { this._handleTimeOptionSelection(TimeSelection.ASAP); }
        });

        // Add "Schedule for Later" option if QR code ordering is not enabled
        if (!this._context.getProperty("qrCode")) {
            data.buttons.push({
                id: TimeSelection.LATER.toString(),
                text: this._localizationService.names("ScheduleForLater"),
                selected: this._item.timeId === TimeSelection.LATER,
                ltag: "ScheduleForLater",
                ariaControls: `div_${p}_time_later`,
                function: () => { this._handleTimeOptionSelection(TimeSelection.LATER); }
            });
        }

        // Render the pill bar UI component
        const pillBar = new PillBar(data, this._localizationService);
        container.appendChild(pillBar.element);
        pillBar.updateBeginningArrows();
    }

    /**
     * Handles the selection of a time option (ASAP or Schedule Later).
     * Updates the selected time and executes the corresponding action.
     *
     * @param timeId - The selected time option (ASAP or Schedule Later).
     */
    private readonly _handleTimeOptionSelection = (timeId: TimeSelection): void => {
        this._item.timeId = timeId;
        this._scheduleLater.clearTimer();

        if (timeId === TimeSelection.ASAP) {
            void this._scheduleLater.releaseLimitedTimeAsync();
            this._asapTimeOption();
        } else {
            this._scheduleLaterTimeOption();
        }

        this._updateTimeOnHeader();
        this._eventBus.emit(EventTypes.SCHEDULE_DATE_CHANGED);
    }

    /**
     * Sets the order to ASAP time selection and hides the "Schedule for Later" section.
     */
    private _asapTimeOption(): void {
        this._item.futureDate = null;
        this._item.futureMinute = null;
        this._onTimeChange?.(TimeSelection.ASAP);
        Util.hideElement(`div_${this._idPrefix}_time_later`);
    }

    /**
     * Initializes the "Schedule for Later" time selection.
     * Clears the section, loads the scheduler component, and displays it.
     */
    private _scheduleLaterTimeOption(): void {
        const div = this._element.querySelector<HTMLElement>(`#div_${this._idPrefix}_time_later`);
        if (!div)
            return;

        div.innerHTML = "";
        div.appendChild(this._scheduleLater.element);
        this._scheduleLater.init(
            this._item.selectedTimeIsExpired,
            this._item.futureDate ?? undefined,
            this._item.futureMinute ?? undefined
        );
        Util.showElement(`div_${this._idPrefix}_time_later`);
        this._item.selectedTimeIsExpired = false;
    }

    /**
     * Clears the error message displayed in the time selection section.
     */
    private _clearErrorMessage(): void {
        const div = this._element.querySelector<HTMLElement>(`#spn_${this._idPrefix}_time_error`);
        if (div) {
            div.innerText = "";
            Util.hideElement(div);
        }
    }

    /**
     * Displays an error message in the time selection section.
     * Allows setting localization attributes for translated messages.
     *
     * @param message - The error message text.
     * @param ltag - (Optional) Localization tag for the message.
     * @param ltagj - (Optional) JSON-based localization object.
     */
    private _setErrorMessage(message: string, ltag?: string, ltagj?: INewName[]): void {
        const div = this._element.querySelector<HTMLElement>(`#spn_${this._idPrefix}_time_error`);
        if (!div)
            return;

        // Clear previous localization attributes
        div.removeAttribute("ltag");
        div.removeAttribute("ltagj");

        div.innerText = message;

        // Apply localization attributes if provided
        if (ltag)
            div.setAttribute("ltag", ltag);

        if (ltagj)
            div.setAttribute("ltagj", Util.toLtagj(ltagj));

        Util.showElement(div);
    }

    /**
     * Checks if ASAP ordering is available based on store operating hours and settings.
     * Displays appropriate error messages if ASAP is unavailable.
     *
     * @returns {boolean} - True if ASAP ordering is available, otherwise false.
     */
    private _isAsapAvailable(): boolean {
        const now = Util.getNowStore(this._data);
        const nowMinutes = Util.getNowStoreMinutes(this._data);
        const storeTimings = Util.getTodayAndSelectedDateTimes(this._data, this._getOrderTypeSubType(), now, nowMinutes);


        // ASAP is currently turned off
        if (!storeTimings.today.ASAP) {
            this._setErrorMessage(this._localizationService.names("ASAP_UNAVAILABLE"), "ASAP_UNAVAILABLE");
            return false;
        }

        // Store is closed today due to a holiday
        if (storeTimings.today.IsClosedHoliday) {
            this._showIsClosedHolidayError(storeTimings);
            return false;
        }

        // Store is currently closed and will reopen soon
        if (storeTimings.today.IsClosedNow && nowMinutes <= storeTimings.today.OpenMinute) {
            const openingTime = this._formatMinutesToTime(storeTimings.today.OpenMinute);
            const name = this._localizationService.names("STORE_CLOSED_OPENS_AT").replace("{{time}}", openingTime);

            const nameObj: INewName[] = this._localizationService.getSupportedLanguages().map(lang => {
                const tempText = this._localizationService.names("STORE_CLOSED_OPENS_AT").replace("{{time}}", openingTime);
                return { lc: lang, text: tempText };
            });

            this._setErrorMessage(name, undefined, nameObj);
            return false;
        }

        // Store is currently closed
        if (storeTimings.today.IsClosedNow && nowMinutes >= storeTimings.today.CloseMinute) {
            this._setErrorMessage(this._localizationService.names("STORE_CLOSED"), "STORE_CLOSED");
            return false;
        }

        // Store is open but will close soon, not enough time to place an order
        if (nowMinutes >= storeTimings.today.CloseMinute - (this._data.getProperty("Settings").OOS.excludeWaitTimeFromHours ? 0 : this._getWaitTime())) {
            const minutesUntilClose = (storeTimings.today.CloseMinute - nowMinutes).toString();
            const name = this._localizationService.names("STORE_CLOSING_SOON").replace("{{minutes}}", minutesUntilClose);

            const nameObj: INewName[] = this._localizationService.getSupportedLanguages().map(lang => {
                const tempText = this._localizationService.names("STORE_CLOSING_SOON", lang).replace("{{minutes}}", minutesUntilClose);
                return { lc: lang, text: tempText };
            });

            this._setErrorMessage(name, undefined, nameObj);
            return false;
        }

        // Store is open but online hours will end soon, not enough time to place an order
        if (nowMinutes >= storeTimings.today.EndOrderMinute - (this._data.getProperty("Settings").OOS.excludeWaitTimeFromHours ? 0 : this._getWaitTime())) {
            const minutesUntilEndOnline = (storeTimings.today.EndOrderMinute - nowMinutes).toString();
            this._displayOnlineHoursEndingError(minutesUntilEndOnline);
            return false;
        }

        // Use OrderTakingStartDayMinute directly from data instead of from storeTimings.today.StartOrderMinute
        const day = now.getDay();
        const hours = this._data.getProperty("Hours");
        const waitTime = this._data.getProperty("Settings").OOS.excludeWaitTimeFromHours ? 0 : (this._getOrderTypeSubType()?.WaitTime || 0);
        const orderTakingStartDayMinute = hours[day] ? hours[day].OrderTakingStartDayMinute + waitTime : 0;

        // Store is not accepting online orders at this time
        if (!storeTimings.today.Online || nowMinutes < orderTakingStartDayMinute) {
            const errorKey = storeTimings.today.Paused ? "ASAP_UNAVAILABLE" : "ONLINE_ORDERS_UNAVAILABLE";
            this._setErrorMessage(this._localizationService.names(errorKey), errorKey);
            return false;
        }

        return true;
    }

    private _showIsClosedHolidayError(storeTimings: { today: IDateStartEndTime, selectedDay: IDateStartEndTime }): void {
        const openingTime = this._formatMinutesToTime(storeTimings.today.OpenMinute);
        const name = this._localizationService.names("STORE_CLOSED_OPENS_AT").replace("{{time}}", openingTime);

        const nameObj: INewName[] = this._localizationService.getSupportedLanguages().map(lang => {
            const tempText = this._localizationService.names("STORE_CLOSED_OPENS_AT").replace("{{time}}", openingTime);
            return { lc: lang, text: tempText };
        });

        this._setErrorMessage(name, undefined, nameObj);
    }

    /**
     * converts minutes from midnight to hours AM/PM
     * @param minutes
     * @returns
     */
    private _formatMinutesToTime(minutes: number): string {
        const hours = Math.floor(minutes / 60);
        const minutesPast = minutes % 60;
        const amPm = (hours >= 12 && hours < 24) ? 'PM' : 'AM';
        const hours12 = hours % 12 || 12;
        const formattedMinutes = minutesPast.toString().padStart(2, '0');
        return `${hours12}:${formattedMinutes} ${amPm}`;
    }

    /**
     * Sets up a periodic check to validate the order's fulfillment time.
     */
    private _setCheckOrderTimeInterval(): void {
        this._checkOrderTimeInterval = setInterval(async () => {
            if (!this._item.checkOrderTimeIntervalIsPaused)
                await this._checkOrderTime();
        }, 10000);
    }

    /**
     * Checks if the current order's fulfillment time is still valid.
     * @returns
     */
    private async _checkOrderTime(): Promise<void> {
        if (!this._order.getProperty("BUSDate"))
            return;

        const businessStartMinute = this._data.getProperty("Settings").BSM;
        const timeOffset = this._data.getProperty("TimeOffset");
        const now = Util.NowStore(timeOffset);
        const nowMinutes = Util.getNowStoreMinutes(this._data);

        const orderTypeSubType = this._order.getProperty("OrderTypeSubType");
        let selectedDate = Util.GetDate_StartTime_EndTime(
            this._data, now, 0, false, orderTypeSubType
        );
        const orderFutureDate = this._order.getProperty("FutureDate");
        const orderFutureMinute = this._order.getProperty("FutureMinute");
        if (orderFutureDate) {
            const busDate = Util.GetBUSDate(Util.DateAdd(orderFutureDate, orderFutureMinute, 'm'), timeOffset, businessStartMinute);

            selectedDate = Util.GetDate_StartTime_EndTime(
                this._data, busDate, orderFutureMinute, false, orderTypeSubType
            );
        }

        if (!selectedDate.IsToday)
            return;

        // 2DO: These cases will need to be implemented later if needed
        //if (DSTET.CloseMinute - nowMinutes <= (aOLO.data.Settings.IWTT ? 0 : aOLO.Order.OrderTypeSubType.WaitTime)) {
        //    aOLO.Dialog.OrderType = new OrderTypeDialog(getOrderTypeFunctions(), true, false);
        //    DialogCreators.messageBoxOk(Names("WillClose").replace("??", (DSTET.CloseMinute - nowMinutes).toString()) + " " + Names("NoTime").replace("???", OnlineOrderingUtil.GetOrderTypeName(aOLO.Order.OrderTypeID, aOLO)), aOLO);
        //} else if (DSTET.CloseMinute - nowMinutes <= (aOLO.data.Settings.IWTT ? 0 : aOLO.Order.OrderTypeSubType.WaitTime) + 5 && !aOLO.Temp.MeetPromiseTimeWarning) {
        //    aOLO.Temp.MeetPromiseTimeWarning = true;
        //    DialogCreators.messageBoxOk(Names("WillClose").replace("??", (DSTET.CloseMinute - nowMinutes).toString()) + " " + Names("FinishX").replace("??", (DSTET.CloseMinute - nowMinutes - aOLO.Order.OrderTypeSubType.WaitTime).toString()), aOLO);
        //} else if (DSTET.EndOrderMinute - nowMinutes <= 0) {
        //    aOLO.Dialog.OrderType = new OrderTypeDialog(getOrderTypeFunctions(), true, false);
        //    DialogCreators.messageBoxOk(Names("MeetPromiseTimeError"), aOLO);
        //} else if (DSTET.EndOrderMinute - nowMinutes <= 5 && !aOLO.Temp.MeetPromiseTimeWarning) {
        //    aOLO.Temp.MeetPromiseTimeWarning = true;
        //    DialogCreators.messageBoxOk(Names("MeetPromiseTimeWarning").replace("??", OnlineOrderingUtil.GetOrderTypeName(aOLO.Order.OrderTypeID, aOLO)), aOLO);
        //}

        // Store is open but online hours will end soon, not enough time to place an order
        if (this._item.timeId == TimeSelection.ASAP && nowMinutes >= selectedDate.EndOrderMinute - (this._data.getProperty("Settings").OOS.excludeWaitTimeFromHours ? 0 : this._order.getProperty("WaitTime"))) {
            const minutesUntilEndOnline = (selectedDate.EndOrderMinute - nowMinutes).toString();
            this._displayOnlineHoursEndingError(minutesUntilEndOnline);

            // enforce selecting a future date and disable asap button
            const laterButton = this._element.querySelector(`#li_${this._idPrefix}_times_${TimeSelection.LATER}`) as HTMLButtonElement;
            laterButton?.click();

            const asapButton = this._element.querySelector(`#li_${this._idPrefix}_times_${TimeSelection.ASAP}`) as HTMLButtonElement;
            if (asapButton) {
                asapButton.style.pointerEvents = "none";
                asapButton.style.opacity = "0.5";
                asapButton.setAttribute("aria-disabled", "true");
                asapButton.tabIndex = -1;
            }

            return;
        }

        // reset the order's fulfillment time if the current fulfillment time is expired
        if (selectedDate.IsToday && orderFutureDate && orderFutureMinute <= nowMinutes + orderTypeSubType.WaitTime && orderFutureMinute !== -1) {
            this._resetFulfillmentTime();
        }
    }

    /**
     * Resets the order's fulfillment time to ASAP and notifies the user.
     */
    private _resetFulfillmentTime(): void {
        const orderType = this._data.getBrandProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === this._order.getProperty("OrderTypeID"));
        const orderTypeName = orderType ? this._localizationService.GetName(orderType.Names) : "Unknown Order Type";

        DialogCreators.messageBoxOk(
            this._localizationService,
            this._dataLayerService,
            this._localizationService.names("FulfillmentTime").replace("???", orderTypeName).replace("??", this._order.getProperty("OrderTypeSubType").WaitTime.toString()),
            true
        );

        this._handleLimitedTimeExpired();
        this._scheduleLater.resetItemModel();
    }

    /**
     * Sets the online hours ending soon error
     * @param minutesUntilEndOnline - The minutes to display until the end of accepting online orders
     */
    private _displayOnlineHoursEndingError(minutesUntilEndOnline: string): void {
        const orderTypeName = OnlineOrderingUtil.GetOrderTypeName(this._order.getProperty("OrderTypeID"), this._data.getProperty("OrderTypeSubTypes"), this._localizationService);
        const name = this._localizationService.names("ONLINE_HOURS_ENDING_SOON").replace("{{order_type}}", orderTypeName).replace("{{minutes}}", minutesUntilEndOnline);

        const nameObj: INewName[] = this._localizationService.getSupportedLanguages().map(lang => {
            const tempText = this._localizationService.names("ONLINE_HOURS_ENDING_SOON", lang).replace("{{minutes}}", minutesUntilEndOnline);
            return { lc: lang, text: tempText };
        });

        this._setErrorMessage(name, undefined, nameObj);
    }
}
